import { Head, router } from "@inertiajs/react"
import { FileText, Lightbulb } from "lucide-react"
import { useState } from "react"

import { EmptyState } from "@/components/empty-state"
import { CorrelationList } from "@/components/insights/correlation-list"
import { DateRangeToggle } from "@/components/insights/date-range-toggle"
import { ExportReportModal } from "@/components/insights/export-report-modal"
import { HeroCorrelationCard } from "@/components/insights/hero-correlation-card"
import { MoodEnergyCards } from "@/components/insights/mood-energy-cards"
import { QuickStats } from "@/components/insights/quick-stats"
import { StreakCard } from "@/components/insights/streak-card"
import { SymptomFrequencyChart } from "@/components/insights/symptom-frequency-chart"
import AppLayout from "@/layouts/app-layout"
import { insightsPath } from "@/routes"

interface Correlation {
  tag: string
  symptom_type: string
  occurrences: number
  total: number
  percentage: number
  strength: string
}

interface InsightsProps {
  range: string
  streak: { current: number; best: number }
  symptom_frequency: { symptom_type: string; count: number }[]
  correlations: Correlation[]
  mood_average: number | null
  energy_average: number | null
  quick_stats: {
    entry_count: number
    symptom_count: number
    meal_count: number
  }
}

const RANGE_LABELS: Record<string, string> = {
  "7d": "Past 7 days",
  "30d": "Past 30 days",
  "90d": "Past 90 days",
  all: "All time",
}

export default function Insights({
  range,
  streak,
  symptom_frequency,
  correlations,
  mood_average,
  energy_average,
  quick_stats,
}: InsightsProps) {
  const rangeLabel = RANGE_LABELS[range] ?? "Past 7 days"
  const [exportOpen, setExportOpen] = useState(false)

  function handleRangeChange(newRange: string) {
    router.get(insightsPath(), { range: newRange }, { preserveScroll: true })
  }

  // First strong correlation becomes the hero card
  const heroCorrelation =
    correlations.find((c) => c.strength === "strong") ?? null

  const isEmpty = quick_stats.entry_count === 0

  return (
    <AppLayout>
      <Head title="Insights" />
      <div className="px-5 pt-5" style={{ paddingBottom: 88 }}>
        {/* Page title with Export button */}
        <div className="mb-4 flex items-center justify-between">
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            Insights
          </h1>
          {!isEmpty && (
            <button
              onClick={() => setExportOpen(true)}
              className="flex items-center gap-2 rounded-xl px-3.5 py-2"
              style={{
                fontSize: 13,
                fontWeight: 600,
                background: "var(--secondary)",
                color: "var(--secondary-foreground)",
                border: "none",
                cursor: "pointer",
              }}
            >
              <FileText style={{ width: 15, height: 15 }} />
              Export
            </button>
          )}
        </div>

        {/* Date range toggle */}
        <div className="mb-5">
          <DateRangeToggle range={range} onRangeChange={handleRangeChange} />
        </div>

        {isEmpty ? (
          <EmptyState
            icon={Lightbulb}
            title="Not enough data yet"
            description="Log for a few more days to start seeing patterns"
          />
        ) : (
          <div className="flex flex-col gap-3">
            <StreakCard current={streak.current} best={streak.best} />
            <HeroCorrelationCard correlation={heroCorrelation} />
            <SymptomFrequencyChart
              symptoms={symptom_frequency}
              rangeLabel={rangeLabel}
            />
            <CorrelationList
              correlations={correlations}
              heroCorrelation={heroCorrelation}
            />
            <MoodEnergyCards
              moodAverage={mood_average}
              energyAverage={energy_average}
            />
            <QuickStats
              entryCount={quick_stats.entry_count}
              symptomCount={quick_stats.symptom_count}
              mealCount={quick_stats.meal_count}
            />
          </div>
        )}
      </div>

      <ExportReportModal
        open={exportOpen}
        onOpenChange={setExportOpen}
        range={range}
      />
    </AppLayout>
  )
}
